import React, { useEffect, useState } from "react";
import { Store, Save, Instagram, Facebook, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useStore } from "../../context/StoreContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const FIELDS = [
  { key: "store_name", label: "Store Name", icon: Store, placeholder: "Aadvan Dry Fruits" },
  { key: "address", label: "Store Address", icon: MapPin, placeholder: "Civil Lines, Sitapur, UP 261001" },
  { key: "phone", label: "Call Number", icon: Phone, placeholder: "+91 90000 00000" },
  { key: "whatsapp", label: "WhatsApp Number (with country code, digits only)", icon: MessageCircle, placeholder: "919000000000" },
  { key: "email", label: "Email", icon: Mail, placeholder: "care@aadvan.in" },
  { key: "instagram", label: "Instagram URL", icon: Instagram, placeholder: "https://instagram.com/yourhandle" },
  { key: "facebook", label: "Facebook URL", icon: Facebook, placeholder: "https://facebook.com/yourpage" },
];

export default function AdminSettings() {
  const { settings } = useStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      store_name: r.data.store_name || "", address: r.data.address || "", phone: r.data.phone || "",
      whatsapp: r.data.whatsapp || "", email: r.data.email || "", instagram: r.data.instagram || "", facebook: r.data.facebook || "",
    }));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.store_name || !form.address || !form.phone || !form.whatsapp || !form.email) {
      toast.error("Store name, address, phone, WhatsApp and email are required"); return;
    }
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved. Changes are now live on your store.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-muted-foreground">Loading settings…</div>;

  return (
    <div className="fade-in max-w-2xl" data-testid="admin-settings">
      <h1 className="font-serif text-4xl text-forest mb-1">Store Settings</h1>
      <p className="text-muted-foreground text-sm mb-8">Update your contact details and social links. These reflect instantly across your storefront (footer, contact page, WhatsApp & call buttons).</p>

      <div className="bg-white border border-border rounded-xl p-6 space-y-5">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="flex items-center gap-2 text-sm text-forest mb-1.5"><f.icon size={15} className="text-gold" /> {f.label}</label>
            <Input value={form[f.key]} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`settings-${f.key}`} className="rounded-lg" />
          </div>
        ))}
        <Button onClick={save} disabled={saving} data-testid="settings-save" className="bg-gold text-forest hover:bg-gold-soft rounded-full h-11 px-8 text-sm uppercase tracking-[0.15em]">
          <Save size={16} className="mr-2" /> {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>

      <div className="mt-6 bg-sand rounded-xl p-5 text-sm text-muted-foreground">
        <span className="text-forest font-medium">Tip:</span> The WhatsApp number should include the country code and digits only (e.g. <span className="text-forest">919812345678</span>) so the "Chat on WhatsApp" button opens correctly.
      </div>
    </div>
  );
}
